/**
 * TenantContext - Unified Multi-Tenant Context
 *
 * Single source of truth for tenant context across the application.
 * Replaces the fragmented approach of ContextSelector + ProjectContext + usePipelineStages.
 *
 * Features:
 * - Unified company/project/pipeline selection
 * - Auto-loading of pipeline when project changes
 * - Platform (OWLY) vs Company user differentiation
 * - Type-safe with well-defined states
 *
 * @module contexts
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { companiesService, type Company } from '@/services/api/companiesService';
import { apiClient } from '@/lib/api-client';
import pipelinesApi, { type PipelineStage } from '@/services/api/pipelinesApi';
import { permissionsKeys } from '@/modules/users/hooks/permissionsQueryKeys';
import { advisorService } from '@/services/api/advisorService';
import { usersService } from '@/services/api/usersService';
import { getHolidays } from '@/utils/holidays';

// ============================================================================
// TYPES
// ============================================================================

export interface TenantProject {
  id: string;
  name: string;
  slug?: string;
  code?: string;
  country?: string;
  company?: string;
  company_name?: string;
  currency?: string;
  status_colors?: Record<string, string>;
  /** Slug of the project's OrbitView for creating quotes */
  orbit_view_project?: string;
  /** User's role in this specific project (from membership) */
  membership_role?: string;
  /** Whether this is the user's primary project */
  is_primary?: boolean;
  /** Whether email templates system is enabled (vs mailto:) */
  email_templates_enabled?: boolean;
  /** Whether portal invitations are enabled for this project */
  send_client_invitation?: boolean;
  /** Company id (for restricted platform users navigating tenant portals) */
  company_id?: string;
  /** Company logo fields propagated so the sidebar can render branding
   *  before the accessible-projects query resolves on cold start. */
  company_logo?: string;
  company_sidebar_icon_light?: string;
  company_sidebar_icon_dark?: string;
}

export interface TenantPipeline {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
}

/** Simplified advisor type for context selection */
export interface TenantAdvisor {
  id: string;
  email: string;
  full_name: string;
  avatar: string | null;
  role: string;
}

export type CompanyFilter = 'all' | string; // 'all' or company UUID

/** Type of pipeline currently being viewed in the UI */
export type ActivePipelineView = 'lead' | 'client' | null;

/**
 * Response from /users/me/accessible-projects/ endpoint
 */
interface AccessibleProjectsResponse {
  user_role: string;
  user_type: string;
  has_all_company_access: boolean;
  company_id: string | null;
  company_name: string | null;
  primary_project_id: string | null;
  projects: TenantProject[];
}

/**
 * Context state for tenant selection
 */
export interface TenantContextState {
  // ─────────────────────────────────────────────────────────────────────────
  // USER INFO
  // ─────────────────────────────────────────────────────────────────────────
  /** True if user belongs to OWLY platform (can see all companies) */
  isPlatform: boolean;
  /** User's global role (admin, manager, sales_advisor, etc.) */
  userRole: string | null;
  /** True if user can see all projects in their company (admin, manager roles) */
  hasAllCompanyAccess: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // SELECTION STATE
  // ─────────────────────────────────────────────────────────────────────────
  /** Current company filter selection */
  selectedCompanyFilter: CompanyFilter;
  /** Selected project (null if none selected) */
  selectedProject: TenantProject | null;
  /** Selected role filter (null = all roles) — filters the advisors/members list */
  selectedRole: string | null;
  /** Selected advisor/member for viewing specific user's data (null = all members) */
  selectedAdvisor: TenantAdvisor | null;

  // ─────────────────────────────────────────────────────────────────────────
  // LOADED DATA
  // ─────────────────────────────────────────────────────────────────────────
  /** Available companies (for platform users) - excludes OWLY platform */
  companies: Company[];
  /** Available companies INCLUDING OWLY platform (for IntegrationsPage) */
  companiesWithPlatform: Company[];
  /** OWLY platform company (for platform-level integrations) */
  platformCompany: Company | null;
  /** Available projects (filtered by selected company) */
  projects: TenantProject[];
  /** Available advisors/members for selected project */
  advisors: TenantAdvisor[];
  /** Available roles (computed from advisors list) */
  availableRoles: string[];
  /** Advisors filtered by selectedRole (same as advisors when no role selected) */
  filteredAdvisors: TenantAdvisor[];
  /** Advisors that the current user can impersonate (filtered by role hierarchy) */
  impersonatableAdvisors: TenantAdvisor[];

  // Lead Pipeline
  /** Lead pipeline stages for selected project */
  leadPipelineStages: PipelineStage[];
  /** Current active lead pipeline */
  leadPipeline: TenantPipeline | null;

  // Client Pipeline
  /** Client pipeline stages for selected project */
  clientPipelineStages: PipelineStage[];
  /** Current active client pipeline */
  clientPipeline: TenantPipeline | null;

  // Broker Pipeline
  /** Broker pipeline stages for selected project */
  brokerPipelineStages: PipelineStage[];
  /** Current active broker pipeline */
  brokerPipeline: TenantPipeline | null;

  // Legacy aliases (for backwards compatibility)
  /** @deprecated Use leadPipelineStages instead */
  pipelineStages: PipelineStage[];
  /** @deprecated Use leadPipeline instead */
  currentPipeline: TenantPipeline | null;

  // ─────────────────────────────────────────────────────────────────────────
  // LOADING STATES
  // ─────────────────────────────────────────────────────────────────────────
  isLoadingCompanies: boolean;
  isLoadingProjects: boolean;
  isLoadingAdvisors: boolean;
  isLoadingLeadPipeline: boolean;
  isLoadingClientPipeline: boolean;
  isLoadingBrokerPipeline: boolean;
  /** @deprecated Use isLoadingLeadPipeline instead */
  isLoadingPipeline: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // COMPUTED VALUES
  // ─────────────────────────────────────────────────────────────────────────
  /** Effective company ID for API calls (null = all companies) */
  effectiveCompanyId: string | null;
  /** Display name for current context */
  contextLabel: string;
  /** True if we have a lead pipeline with stages */
  canShowLeadPipelineStats: boolean;
  /** True if we have a client pipeline with stages */
  canShowClientPipelineStats: boolean;
  /** @deprecated Use canShowLeadPipelineStats instead */
  canShowPipelineStats: boolean;
  /** True if viewing all companies (no specific company selected) */
  isAllCompaniesView: boolean;
  /** True when transitioning between projects/companies (prevents race conditions) */
  isInTransition: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIVE PIPELINE VIEW (for sidebar highlighting)
  // ─────────────────────────────────────────────────────────────────────────
  /** Type of pipeline currently being viewed ('lead' | 'client' | null) */
  activePipelineView: ActivePipelineView;
  /** Set which pipeline type is being viewed (used by PipelineConfigPage) */
  setActivePipelineView: (type: ActivePipelineView) => void;

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────
  /** Set company filter */
  setCompanyFilter: (filter: CompanyFilter) => void;
  /** Set selected project by ID */
  setSelectedProjectId: (id: string | null) => void;
  /** Set selected project directly */
  setSelectedProject: (project: TenantProject | null) => void;
  /** Set selected role filter (null = all roles) */
  setSelectedRole: (role: string | null) => void;
  /** Set selected advisor/member directly */
  setSelectedAdvisor: (advisor: TenantAdvisor | null) => void;
  /** Set selected advisor/member by ID */
  setSelectedAdvisorId: (id: string | null) => void;
  /** Clear all selections */
  clearSelection: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

// Role hierarchy for impersonation validation (mirrors backend User.ROLE_HIERARCHY)
export const ROLE_HIERARCHY: Record<string, number> = {
  owly_admin: 100,
  owly_support: 90,
  owly_3d: 85,
  owly_reviewer: 80,
  owly_agency: 75,
  owly_broker: 74,
  admin: 60,
  manager: 50,
  project_manager: 45,
  sales_leader: 40,
  sales_advisor: 30,
  sales: 30,
  marketing: 25,
  support: 20,
  reviewer: 15,
};

const TenantContext = createContext<TenantContextState | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface TenantProviderProps {
  children: ReactNode;
  /** Default company filter for platform users */
  defaultCompanyFilter?: CompanyFilter;
}

// ============================================================================
// LOCALSTORAGE PERSISTENCE
// ============================================================================

const STORAGE_KEY_COMPANY = 'owly_tenant_company';
const STORAGE_KEY_PROJECT = 'owly_tenant_project';
const STORAGE_KEY_ADVISOR = 'owly_tenant_advisor';

function loadStoredCompanyFilter(): CompanyFilter {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_COMPANY);
    if (stored) {
      return stored as CompanyFilter;
    }
  } catch (e) {
    console.warn('[TenantContext] Error reading company from localStorage:', e);
  }
  return 'all';
}

function loadStoredProject(): TenantProject | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PROJECT);
    if (stored) {
      return JSON.parse(stored) as TenantProject;
    }
  } catch (e) {
    console.warn('[TenantContext] Error reading project from localStorage:', e);
  }
  return null;
}

function saveCompanyFilter(filter: CompanyFilter): void {
  try {
    localStorage.setItem(STORAGE_KEY_COMPANY, filter);
  } catch (e) {
    console.warn('[TenantContext] Error saving company to localStorage:', e);
  }
}

function saveProject(project: TenantProject | null): void {
  try {
    if (project) {
      localStorage.setItem(STORAGE_KEY_PROJECT, JSON.stringify(project));
    } else {
      localStorage.removeItem(STORAGE_KEY_PROJECT);
    }
  } catch (e) {
    console.warn('[TenantContext] Error saving project to localStorage:', e);
  }
}

function loadStoredAdvisor(): TenantAdvisor | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ADVISOR);
    if (stored) {
      return JSON.parse(stored) as TenantAdvisor;
    }
  } catch (e) {
    console.warn('[TenantContext] Error reading advisor from localStorage:', e);
  }
  return null;
}

function saveAdvisor(advisor: TenantAdvisor | null): void {
  try {
    if (advisor) {
      localStorage.setItem(STORAGE_KEY_ADVISOR, JSON.stringify(advisor));
    } else {
      localStorage.removeItem(STORAGE_KEY_ADVISOR);
    }
  } catch (e) {
    console.warn('[TenantContext] Error saving advisor to localStorage:', e);
  }
}

export function TenantProvider({
  children,
  defaultCompanyFilter = 'all',
}: TenantProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const { isPlatform, isLoading: isLoadingEntitlements } = useEntitlements();
  // Only specific OWLY roles get unrestricted platform access (see all projects/companies).
  // Agency users and platform company members with tenant-level roles (e.g., sales_advisor
  // in the Owly company) should use the accessible-projects endpoint instead.
  const UNRESTRICTED_PLATFORM_ROLES = ['owly_admin', 'owly_support', 'owly_3d', 'owly_reviewer'];
  const isAgencyUser = user?.role === 'owly_agency';
  const isBrokerUser = user?.role === 'owly_broker';
  const isRestrictedPlatformUser = isAgencyUser || isBrokerUser;
  const isUnrestrictedPlatform = isPlatform && (
    user?.is_superuser ||
    user?.is_staff ||
    UNRESTRICTED_PLATFORM_ROLES.includes(user?.role || '')
  );
  const queryClient = useQueryClient();

  // ─────────────────────────────────────────────────────────────────────────
  // SELECTION STATE (with localStorage persistence)
  // ─────────────────────────────────────────────────────────────────────────

  // Initialize from localStorage or default
  const [selectedCompanyFilter, setSelectedCompanyFilterState] = useState<CompanyFilter>(() => {
    const stored = loadStoredCompanyFilter();
    return stored !== 'all' ? stored : defaultCompanyFilter;
  });

  const [selectedProject, setSelectedProjectState] = useState<TenantProject | null>(() => {
    return loadStoredProject();
  });

  // Role filter (filters advisor/member list)
  const [selectedRole, setSelectedRoleState] = useState<string | null>(null);

  // Advisor/member selection
  const [selectedAdvisor, setSelectedAdvisorState] = useState<TenantAdvisor | null>(() => {
    return loadStoredAdvisor();
  });

  // Track which pipeline type is being viewed (for sidebar highlighting)
  const [activePipelineView, setActivePipelineView] = useState<ActivePipelineView>(null);

  // Track when we're transitioning between projects/companies (to prevent race conditions)
  const [isInTransition, setIsInTransition] = useState(false);
  const transitionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);


  // Flag to prevent infinite loop when dispatching/receiving projectChanged events
  const isDispatchingEventRef = React.useRef(false);

  // Wrapper setters that persist to localStorage
  const setSelectedCompanyFilter = useCallback((filter: CompanyFilter) => {
    setSelectedCompanyFilterState((prevFilter) => {
      // Only update if different
      if (filter !== prevFilter) {
        // Start transition - this prevents race conditions during company/project switch
        setIsInTransition(true);

        // Clear any existing transition timeout
        if (transitionTimeoutRef.current) {
          clearTimeout(transitionTimeoutRef.current);
        }

        saveCompanyFilter(filter);
        // When changing company, clear project and advisor - user must select manually
        setSelectedProjectState(null);
        saveProject(null);
        setSelectedAdvisorState(null);
        saveAdvisor(null);

        // IMPORTANT: Invalidate all project-dependent caches immediately
        // This prevents showing stale data from the previous company/project
        queryClient.invalidateQueries({ queryKey: ['stages'] });
        queryClient.invalidateQueries({ queryKey: ['blocks'] });
        queryClient.invalidateQueries({ queryKey: ['units'] });
        queryClient.invalidateQueries({ queryKey: ['units-paginated'] });
        queryClient.invalidateQueries({ queryKey: ['units-infinite'] });
        queryClient.invalidateQueries({ queryKey: ['typologies'] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        queryClient.invalidateQueries({ queryKey: ['amenities'] });

        // End transition after a short delay
        transitionTimeoutRef.current = setTimeout(() => {
          setIsInTransition(false);
        }, 100);
      }
      return filter;
    });
  }, [queryClient]);

  const setSelectedProject = useCallback((project: TenantProject | null) => {
    setSelectedProjectState(project);
    advisorExplicitlyClearedRef.current = false; // Reset so auto-select works for new project
    saveProject(project);

    // When changing project, clear advisor (will be auto-selected or manually chosen)
    setSelectedAdvisorState(null);
    saveAdvisor(null);

    // ─────────────────────────────────────────────────────────────────────────
    // CACHE INVALIDATION: Invalidate all project-dependent queries
    // This ensures data refreshes when switching between projects
    // ─────────────────────────────────────────────────────────────────────────
    queryClient.invalidateQueries({ queryKey: ['stages'] });
    queryClient.invalidateQueries({ queryKey: ['blocks'] });
    queryClient.invalidateQueries({ queryKey: ['units'] });
    queryClient.invalidateQueries({ queryKey: ['typologies'] });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['quotes'] });
    queryClient.invalidateQueries({ queryKey: ['orbit-views'] });
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
    queryClient.invalidateQueries({ queryKey: ['amenities'] });

    // Invalidate permissions (may be project-scoped)
    queryClient.invalidateQueries({ queryKey: permissionsKeys.me() });
    if (project?.id) {
      queryClient.invalidateQueries({ queryKey: permissionsKeys.meWithProject(project.id) });
    }

    // CRITICAL: Refetch the correct key based on whether project is selected
    // usePermissions uses meWithProject(projectId) when a project is selected
    if (project?.id) {
      queryClient.refetchQueries({ queryKey: permissionsKeys.meWithProject(project.id) });
    } else {
      queryClient.refetchQueries({ queryKey: permissionsKeys.me() });
    }

    // Emit event for any components that need to react to project changes
    // Set flag to prevent infinite loop when we catch our own event
    isDispatchingEventRef.current = true;
    window.dispatchEvent(new CustomEvent('projectChanged', {
      detail: { project }
    }));
    isDispatchingEventRef.current = false;

    // End transition IMMEDIATELY when project is set
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    queueMicrotask(() => {
      setIsInTransition(false);
    });
  }, [queryClient]);

  // Advisor setter (platform users only)
  const setSelectedAdvisor = useCallback((advisor: TenantAdvisor | null) => {
    setSelectedAdvisorState(advisor);
    saveAdvisor(advisor);

    // Invalidate calendar queries when advisor changes
    queryClient.invalidateQueries({ queryKey: ['calendar'] });
  }, [queryClient]);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH COMPANIES (unrestricted platform users only — not agency)
  // ─────────────────────────────────────────────────────────────────────────

  const {
    data: companiesData,
    isLoading: isLoadingCompanies,
  } = useQuery({
    queryKey: ['tenant-companies'],
    queryFn: () => companiesService.getCompanies({ page_size: 100 }),
    enabled: isUnrestrictedPlatform,
    staleTime: 5 * 60 * 1000,
  });

  // ─────────────────────────────────────────────────────────────────────────
  // COMPUTE EFFECTIVE COMPANY ID
  // ─────────────────────────────────────────────────────────────────────────

  const effectiveCompanyId = useMemo(() => {
    if (!isPlatform) {
      // Regular user: always their own company
      return user?.company || null;
    }
    // Platform user (including agency)
    if (selectedCompanyFilter === 'all') {
      return null; // No filter = all companies
    }
    return selectedCompanyFilter; // Specific company UUID
  }, [isPlatform, selectedCompanyFilter, user?.company]);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH PROJECTS
  // - Platform users: fetch via /projects/ with company filter
  // - Tenant users: fetch via /users/me/accessible-projects/ for proper access control
  // ─────────────────────────────────────────────────────────────────────────

  // Unrestricted platform users: fetch all projects (optionally filtered by company)
  const {
    data: platformProjectsData,
    isLoading: isLoadingPlatformProjects,
  } = useQuery({
    queryKey: ['tenant-projects-platform', effectiveCompanyId],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: 100 };
      if (effectiveCompanyId) {
        params.company = effectiveCompanyId;
      }
      const response = await apiClient.get<{ results: TenantProject[] }>('/projects/', { params });
      return response;
    },
    enabled: isAuthenticated && !isLoadingEntitlements && isUnrestrictedPlatform,
    staleTime: 5 * 60 * 1000,
  });

  // Tenant users + agency: fetch via accessible-projects endpoint for proper access control
  // Shares the queryKey with ProjectContext so TanStack Query dedupes: the
  // hook subscribes to the same cache entry instead of firing a second request.
  // Polling interval lifted to 5 minutes — admin-side permission changes
  // explicitly invalidate this query, so the 30s safety net was unnecessary.
  const {
    data: accessibleProjectsData,
    isLoading: isLoadingAccessibleProjects,
  } = useQuery({
    queryKey: ['accessible-projects', user?.id],
    queryFn: async () => {
      const response = await apiClient.get<AccessibleProjectsResponse>('/auth/users/me/accessible-projects/');
      return response;
    },
    enabled: isAuthenticated && !isLoadingEntitlements && !isUnrestrictedPlatform,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  // ─────────────────────────────────────────────────────────────────────────
  // DERIVE COMPANIES (must be AFTER accessible-projects query)
  // - Unrestricted platform: from /companies/ API
  // - Agency: derived from accessible projects
  // ─────────────────────────────────────────────────────────────────────────

  // All companies including platform (from API for unrestricted, derived for agency)
  const companiesWithPlatform = useMemo(() => {
    let list: any[];
    if (isUnrestrictedPlatform) {
      list = companiesData?.results || [];
    } else if (isRestrictedPlatformUser && accessibleProjectsData?.projects) {
      // Agency/Broker: derive companies from their accessible projects
      const companyMap = new Map<string, any>();
      for (const project of accessibleProjectsData.projects) {
        const p = project as any;
        const companyId = p.company_id || p.company;
        if (companyId && !companyMap.has(companyId)) {
          companyMap.set(companyId, {
            id: companyId,
            name: p.company_name || 'Empresa',
            logo: p.company_logo || '',
            sidebar_icon_light: p.company_sidebar_icon_light || '',
            sidebar_icon_dark: p.company_sidebar_icon_dark || '',
            // Brand palette + portal theme — needed by PortalThemeProvider so
            // the broker portal picks up the tenant colors without an extra fetch.
            primary_color: p.company_primary_color || '',
            secondary_color: p.company_secondary_color || '',
            portal_theme: p.company_portal_theme || {},
            is_platform: false,
          });
        }
      }
      list = Array.from(companyMap.values());
    } else {
      list = companiesData?.results || [];
    }
    return list.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
  }, [isUnrestrictedPlatform, isRestrictedPlatformUser, companiesData, accessibleProjectsData]);

  // Filter out OWLY platform company from list (default for most pages)
  const companies = useMemo(() => {
    return companiesWithPlatform
      .filter((c: any) => !c.is_platform)
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
  }, [companiesWithPlatform]);

  // Extract OWLY platform company (for platform-level integrations)
  const platformCompany = useMemo(() => {
    return companiesWithPlatform.find((c: any) => c.is_platform) || null;
  }, [companiesWithPlatform]);

  // ─────────────────────────────────────────────────────────────────────────
  // COMBINE PROJECTS
  // ─────────────────────────────────────────────────────────────────────────

  // Combine project data from both sources
  const projects = useMemo(() => {
    let list: any[];
    if (isUnrestrictedPlatform) {
      list = platformProjectsData?.results || [];
    } else {
      const allAccessible = accessibleProjectsData?.projects || [];
      // Agency/Broker: filter by selected company (like platform users do)
      if (isRestrictedPlatformUser && selectedCompanyFilter && selectedCompanyFilter !== 'all') {
        list = allAccessible.filter((p: any) => p.company_id === selectedCompanyFilter || p.company === selectedCompanyFilter);
      } else {
        list = allAccessible;
      }
    }
    return list.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
  }, [isUnrestrictedPlatform, isRestrictedPlatformUser, platformProjectsData, accessibleProjectsData, selectedCompanyFilter]);

  const isLoadingProjects = isUnrestrictedPlatform ? isLoadingPlatformProjects : isLoadingAccessibleProjects;

  // Extract user access info from accessible projects response
  const hasAllCompanyAccess = accessibleProjectsData?.has_all_company_access ?? isUnrestrictedPlatform;
  const userRole = accessibleProjectsData?.user_role ?? user?.role ?? null;
  const primaryProjectId = accessibleProjectsData?.primary_project_id ?? null;

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH ADVISORS (platform users + managers/admins)
  // ─────────────────────────────────────────────────────────────────────────

  const canViewAdvisors = isPlatform || ['manager', 'project_manager', 'admin', 'owly_admin', 'sales_leader'].includes(userRole || '');

  const {
    data: advisorsData,
    isLoading: isLoadingAdvisors,
  } = useQuery({
    queryKey: ['tenant-advisors', effectiveCompanyId, selectedProject?.id],
    queryFn: async () => {
      const params: { project_id?: string; company_id?: string } = {};
      if (selectedProject?.id) {
        params.project_id = selectedProject.id;
      }
      if (effectiveCompanyId) {
        params.company_id = effectiveCompanyId;
      }
      return advisorService.getAll(params);
    },
    enabled: canViewAdvisors && isAuthenticated && (!!effectiveCompanyId || !!selectedProject?.id),
    staleTime: 5 * 60 * 1000,
  });

  // Map advisors to simplified TenantAdvisor type (project-scoped, for dashboards)
  const advisors: TenantAdvisor[] = useMemo(() => {
    if (!advisorsData) return [];
    return advisorsData.map(a => ({
      id: a.id,
      email: a.email,
      full_name: a.full_name,
      avatar: a.avatar,
      role: a.role,
    }));
  }, [advisorsData]);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH ALL COMPANY MEMBERS (for role + member filtering in Omnichannel etc.)
  // ─────────────────────────────────────────────────────────────────────────

  const {
    data: companyMembersData,
  } = useQuery({
    queryKey: ['tenant-members', effectiveCompanyId],
    queryFn: async () => {
      return usersService.getUsers({ company: effectiveCompanyId, is_active: true, limit: 200 });
    },
    enabled: canViewAdvisors && isAuthenticated && !!effectiveCompanyId,
    staleTime: 5 * 60 * 1000,
  });

  // Map company members to TenantAdvisor type, filtered by project when selected
  // Managers/admins without project assignments are always included (company-wide access)
  const MANAGER_ROLES = ['manager', 'project_manager', 'admin', 'owly_admin', 'sales_leader'];

  const companyMembers: TenantAdvisor[] = useMemo(() => {
    if (!companyMembersData?.results) return [];

    const allMembers = companyMembersData.results.map((u: any) => ({
      id: u.id,
      email: u.email,
      full_name: u.full_name || u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
      avatar: u.avatar || null,
      role: u.role || '',
      assignedProjects: u.assigned_projects || [],
    }));

    // If a project is selected, filter to: users assigned to that project + managers without project assignments
    if (selectedProject?.id) {
      return allMembers.filter((u: any) => {
        const isAssignedToProject = u.assignedProjects.includes(selectedProject.id);
        const isManagerWithoutProjects = MANAGER_ROLES.includes(u.role) && u.assignedProjects.length === 0;
        return isAssignedToProject || isManagerWithoutProjects;
      });
    }

    return allMembers;
  }, [companyMembersData, selectedProject?.id]);

  // Track when user explicitly clears advisor (selects "Todos los asesores")
  // This prevents auto-select from overriding an intentional "show all" choice
  const advisorExplicitlyClearedRef = React.useRef(false);

  // Helper to set advisor/member by ID — searches both company members and project advisors
  const setSelectedAdvisorId = useCallback((id: string | null) => {
    if (!id) {
      advisorExplicitlyClearedRef.current = true;
      setSelectedAdvisor(null);
      return;
    }
    advisorExplicitlyClearedRef.current = false;
    const member = companyMembers.find(a => a.id === id) || advisors.find(a => a.id === id);
    if (member) {
      setSelectedAdvisor(member);
    }
  }, [companyMembers, advisors, setSelectedAdvisor]);

  // ─────────────────────────────────────────────────────────────────────────
  // ROLE FILTER + FILTERED ADVISORS
  // ─────────────────────────────────────────────────────────────────────────

  // Use companyMembers for roles/filtering (broader than project-scoped advisors)
  const memberPool = companyMembers.length > 0 ? companyMembers : advisors;

  // Compute available roles from all company members
  const availableRoles = useMemo(() => {
    const roles = new Set(memberPool.map(a => a.role).filter(Boolean));
    return Array.from(roles).sort();
  }, [memberPool]);

  // Filter members by selected role
  const filteredAdvisors = useMemo(() => {
    if (!selectedRole) return memberPool;
    return memberPool.filter(a => a.role === selectedRole);
  }, [memberPool, selectedRole]);

  // Advisors filtered by role hierarchy (for impersonation — only show lower roles)
  const impersonatableAdvisors = useMemo(() => {
    const myLevel = ROLE_HIERARCHY[user?.role || ''] || 0;
    return filteredAdvisors.filter(a => {
      const theirLevel = ROLE_HIERARCHY[a.role || ''] || 0;
      if (isPlatform) {
        // Platform users cannot impersonate other platform users
        return !a.role?.startsWith('owly_') && theirLevel < myLevel;
      }
      return theirLevel < myLevel;
    });
  }, [filteredAdvisors, user?.role, isPlatform]);

  // Set role filter
  const setSelectedRole = useCallback((role: string | null) => {
    setSelectedRoleState(role);
    // Clear advisor if current advisor doesn't match new role
    if (role && selectedAdvisor && selectedAdvisor.role !== role) {
      advisorExplicitlyClearedRef.current = true;
      setSelectedAdvisor(null);
    }
  }, [selectedAdvisor, setSelectedAdvisor]);

  // Reset role when project/company changes
  useEffect(() => {
    setSelectedRoleState(null);
  }, [effectiveCompanyId, selectedProject?.id]);

  // Auto-select first advisor when data loads (platform users + managers)
  // This ensures dashboard always shows data for a specific advisor
  // Skip if user explicitly chose "Todos"
  useEffect(() => {
    if (!canViewAdvisors || memberPool.length === 0) return;
    if (advisorExplicitlyClearedRef.current) return;

    // Use filtered list if role is selected
    const pool = selectedRole ? filteredAdvisors : memberPool;
    if (pool.length === 0) return;

    // If no advisor selected or selected advisor not in pool, select first
    if (!selectedAdvisor || !pool.some(a => a.id === selectedAdvisor.id)) {
      setSelectedAdvisor(pool[0]);
    }
  }, [canViewAdvisors, memberPool, filteredAdvisors, selectedRole, selectedAdvisor, setSelectedAdvisor]);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH LEAD PIPELINE & STAGES
  // ─────────────────────────────────────────────────────────────────────────

  const {
    data: leadPipelineData,
    isLoading: isLoadingLeadPipeline,
  } = useQuery({
    queryKey: ['tenant-pipeline', 'lead', selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject?.id) return null;

      const listResponse = await pipelinesApi.configurations.list({
        project: selectedProject.id,
        type: 'lead',
        is_active: true,
      });

      if (!listResponse.results || listResponse.results.length === 0) {
        return { pipeline: null, stages: [] };
      }

      const pipelineId = listResponse.results[0].id;
      const pipeline = await pipelinesApi.configurations.get(pipelineId);
      const stages = (pipeline.stages || []).sort((a, b) => a.order_position - b.order_position);

      return {
        pipeline: { id: pipeline.id, name: pipeline.name, type: pipeline.type, is_active: pipeline.is_active },
        stages,
      };
    },
    enabled: isAuthenticated && !!selectedProject?.id,
    staleTime: 5 * 60 * 1000,
  });

  const leadPipeline = leadPipelineData?.pipeline || null;
  const leadPipelineStages = leadPipelineData?.stages || [];

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH CLIENT PIPELINE & STAGES
  // ─────────────────────────────────────────────────────────────────────────

  const {
    data: clientPipelineData,
    isLoading: isLoadingClientPipeline,
  } = useQuery({
    queryKey: ['tenant-pipeline', 'client', selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject?.id) return null;

      const listResponse = await pipelinesApi.configurations.list({
        project: selectedProject.id,
        type: 'client',
        is_active: true,
      });

      if (!listResponse.results || listResponse.results.length === 0) {
        return { pipeline: null, stages: [] };
      }

      const pipelineId = listResponse.results[0].id;
      const pipeline = await pipelinesApi.configurations.get(pipelineId);
      const stages = (pipeline.stages || []).sort((a, b) => a.order_position - b.order_position);

      return {
        pipeline: { id: pipeline.id, name: pipeline.name, type: pipeline.type, is_active: pipeline.is_active },
        stages,
      };
    },
    enabled: isAuthenticated && !!selectedProject?.id,
    staleTime: 5 * 60 * 1000,
  });

  const clientPipeline = clientPipelineData?.pipeline || null;
  const clientPipelineStages = clientPipelineData?.stages || [];

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH BROKER PIPELINE & STAGES
  // ─────────────────────────────────────────────────────────────────────────

  const {
    data: brokerPipelineData,
    isLoading: isLoadingBrokerPipeline,
  } = useQuery({
    queryKey: ['tenant-pipeline', 'broker', selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject?.id) return null;

      const listResponse = await pipelinesApi.configurations.list({
        project: selectedProject.id,
        type: 'broker' as any,
        is_active: true,
      });

      if (!listResponse.results || listResponse.results.length === 0) {
        return { pipeline: null, stages: [] };
      }

      const pipelineId = listResponse.results[0].id;
      const pipeline = await pipelinesApi.configurations.get(pipelineId);
      const stages = (pipeline.stages || []).sort((a: any, b: any) => a.order_position - b.order_position);

      return {
        pipeline: { id: pipeline.id, name: pipeline.name, type: pipeline.type, is_active: pipeline.is_active },
        stages,
      };
    },
    enabled: isAuthenticated && !!selectedProject?.id,
    staleTime: 5 * 60 * 1000,
  });

  const brokerPipeline = brokerPipelineData?.pipeline || null;
  const brokerPipelineStages = brokerPipelineData?.stages || [];

  // Legacy aliases for backwards compatibility
  const currentPipeline = leadPipeline;
  const pipelineStages = leadPipelineStages;
  const isLoadingPipeline = isLoadingLeadPipeline;

  // ─────────────────────────────────────────────────────────────────────────
  // SYNC WITH ProjectContext (listens to projectChanged events)
  // This ensures TenantContext stays in sync when user selects project
  // via ProjectSelector component
  // ─────────────────────────────────────────────────────────────────────────

  // Initial sync on mount - check if ProjectContext has a different project
  useEffect(() => {
    const projectContextProjectId = localStorage.getItem('owly_current_project_id');

    // If ProjectContext has a project selected that's different from TenantContext
    if (projectContextProjectId && projectContextProjectId !== selectedProject?.id) {
      // Find the project in our available projects
      const projectFromList = projects.find(p => p.id === projectContextProjectId);

      if (projectFromList) {
        // Sync company filter if platform user
        if (projectFromList.company && isPlatform) {
          setSelectedCompanyFilter(projectFromList.company);
        }

        setSelectedProject(projectFromList);
      }
    }
  // Only run once when projects are loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.length > 0]);

  // Listen for projectChanged events from ProjectContext (legacy compatibility)
  // This syncs TenantContext when ProjectContext changes the project
  useEffect(() => {
    const handleProjectChanged = (event: CustomEvent<{ project: TenantProject | null }>) => {
      // Skip if this event was dispatched by TenantContext itself (prevents infinite loop)
      if (isDispatchingEventRef.current) {
        return;
      }

      const newProject = event.detail?.project;

      // Only sync if the project is different
      if (newProject?.id !== selectedProject?.id) {
        // If there's a project, also sync the company filter
        if (newProject?.company && isPlatform) {
          setSelectedCompanyFilter(newProject.company);
        }

        setSelectedProject(newProject);
      }
    };

    window.addEventListener('projectChanged', handleProjectChanged as EventListener);

    return () => {
      window.removeEventListener('projectChanged', handleProjectChanged as EventListener);
    };
  }, [selectedProject?.id, isPlatform, setSelectedCompanyFilter, setSelectedProject]);

  // ─────────────────────────────────────────────────────────────────────────
  // SYNC selectedProject fields from fresh API data (e.g. email_templates_enabled)
  // Uses a ref to track last-synced ID+version and avoid infinite loops
  // ─────────────────────────────────────────────────────────────────────────
  const lastSyncedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!selectedProject || projects.length === 0) return;
    const freshProject = projects.find(p => p.id === selectedProject.id);
    if (!freshProject) return;

    // Build a fingerprint of the fields we care about
    const syncKeys: (keyof TenantProject)[] = [
      'name', 'slug', 'code', 'country', 'currency',
      'orbit_view_project', 'membership_role', 'is_primary',
      'email_templates_enabled',
      'company_id', 'company_name',
      'company_logo', 'company_sidebar_icon_light', 'company_sidebar_icon_dark',
    ];
    const fingerprint = syncKeys.map(k => `${k}=${(freshProject as any)[k]}`).join('|');
    if (lastSyncedRef.current === fingerprint) return;

    const needsSync = syncKeys.some(k => (freshProject as any)[k] !== (selectedProject as any)[k]);
    if (needsSync) {
      const synced: TenantProject = { ...selectedProject };
      for (const key of syncKeys) {
        if (key in freshProject) {
          (synced as any)[key] = (freshProject as any)[key];
        }
      }
      lastSyncedRef.current = fingerprint;
      setSelectedProjectState(synced);
      saveProject(synced);
    } else {
      lastSyncedRef.current = fingerprint;
    }
  }, [projects, selectedProject]);

  // ─────────────────────────────────────────────────────────────────────────
  // PROJECT VALIDATION
  // - Platform users: NO auto-select, user must choose manually
  // - Tenant users: auto-select primary project ONLY on initial load
  // - Clear project if it's not in available projects (company changed)
  // ─────────────────────────────────────────────────────────────────────────

  // Track if initial auto-select has been done for tenant users
  const initialAutoSelectDoneRef = React.useRef(false);

  useEffect(() => {
    // Skip if viewing all companies (platform users only) - clear project
    if (isPlatform && selectedCompanyFilter === 'all') {
      setSelectedProject(null);
      return;
    }

    // Wait for projects to load
    if (projects.length === 0) {
      return;
    }

    // If there's a selected project, validate it's still in the list
    if (selectedProject) {
      const projectInList = projects.some(p => p.id === selectedProject.id);
      if (!projectInList) {
        // Project not available anymore (company changed or access revoked)
        // Clear the selection - user must choose manually
        setSelectedProject(null);
      }
      return;
    }

    // No project selected - auto-select ONLY for tenant users on initial load
    // Platform users must always select manually
    if (isPlatform) {
      // Platform users (including agency): NO auto-select, they must choose manually
      return;
    }

    // Tenant users: auto-select primary project ONLY on initial load
    if (!initialAutoSelectDoneRef.current) {
      initialAutoSelectDoneRef.current = true;

      // Prefer primary project if set
      if (primaryProjectId) {
        const primaryProject = projects.find(p => p.id === primaryProjectId);
        if (primaryProject) {
          setSelectedProject(primaryProject);
          return;
        }
      }

      // If only one project, auto-select it
      if (projects.length === 1) {
        setSelectedProject(projects[0]);
      }
      // Otherwise, user must select manually
    }
  }, [isPlatform, selectedCompanyFilter, projects, selectedProject, primaryProjectId, setSelectedProject]);

  // ─────────────────────────────────────────────────────────────────────────
  // COMPUTED VALUES
  // ─────────────────────────────────────────────────────────────────────────

  const isAllCompaniesView = selectedCompanyFilter === 'all';

  const contextLabel = useMemo(() => {
    if (!isPlatform) {
      return selectedProject?.name || 'Mi Empresa';
    }
    if (isAllCompaniesView) {
      return 'Todas las Compañías';
    }
    const company = companies.find((c: any) => c.id === selectedCompanyFilter);
    const companyName = company?.name || 'Compañía';
    return selectedProject ? `${companyName} → ${selectedProject.name}` : companyName;
  }, [isPlatform, isAllCompaniesView, selectedCompanyFilter, companies, selectedProject]);

  const canShowLeadPipelineStats = useMemo(() => {
    return !!selectedProject && leadPipelineStages.length > 0;
  }, [selectedProject, leadPipelineStages]);

  const canShowClientPipelineStats = useMemo(() => {
    return !!selectedProject && clientPipelineStages.length > 0;
  }, [selectedProject, clientPipelineStages]);

  // Legacy alias
  const canShowPipelineStats = canShowLeadPipelineStats;

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS (use wrapper setters with localStorage persistence)
  // ─────────────────────────────────────────────────────────────────────────

  const setCompanyFilter = useCallback((filter: CompanyFilter) => {
    setSelectedCompanyFilter(filter);
  }, [setSelectedCompanyFilter]);

  const setSelectedProjectId = useCallback((id: string | null) => {
    if (!id) {
      setSelectedProject(null);
      return;
    }
    const project = projects.find(p => p.id === id);
    if (project) {
      setSelectedProject(project);
    }
  }, [projects, setSelectedProject]);

  const clearSelection = useCallback(() => {
    setSelectedCompanyFilter(defaultCompanyFilter);
    setSelectedProject(null);
    setSelectedAdvisor(null);
    // Also clear localStorage
    saveCompanyFilter('all');
    saveProject(null);
    saveAdvisor(null);
  }, [defaultCompanyFilter, setSelectedCompanyFilter, setSelectedProject, setSelectedAdvisor]);

  // ─────────────────────────────────────────────────────────────────────────
  // PRE-LOAD HOLIDAYS
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const countryCode = selectedProject?.country;
    if (!countryCode) return;
    const year = new Date().getFullYear();
    // Pre-cargar festivos del año actual y siguiente en localStorage
    getHolidays(year, countryCode).catch(() => {});
    getHolidays(year + 1, countryCode).catch(() => {});
  }, [selectedProject?.id, selectedProject?.country]);

  // ─────────────────────────────────────────────────────────────────────────
  // CONTEXT VALUE
  // ─────────────────────────────────────────────────────────────────────────

  const value: TenantContextState = {
    // User info
    isPlatform,
    userRole,
    hasAllCompanyAccess,

    // Selection state
    selectedCompanyFilter,
    selectedProject,
    selectedRole,
    selectedAdvisor,

    // Loaded data
    companies,
    companiesWithPlatform,
    platformCompany,
    projects,
    advisors,
    availableRoles,
    filteredAdvisors,
    impersonatableAdvisors,

    // Lead Pipeline
    leadPipelineStages,
    leadPipeline,

    // Client Pipeline
    clientPipelineStages,
    clientPipeline,

    // Broker Pipeline
    brokerPipelineStages,
    brokerPipeline,

    // Legacy aliases (backwards compatibility)
    pipelineStages,
    currentPipeline,

    // Loading states
    isLoadingCompanies,
    isLoadingProjects,
    isLoadingAdvisors,
    isLoadingLeadPipeline,
    isLoadingClientPipeline,
    isLoadingBrokerPipeline,
    isLoadingPipeline,

    // Computed values
    effectiveCompanyId,
    contextLabel,
    canShowLeadPipelineStats,
    canShowClientPipelineStats,
    canShowPipelineStats,
    isAllCompaniesView,
    isInTransition,

    // Active pipeline view (for sidebar)
    activePipelineView,
    setActivePipelineView,

    // Actions
    setCompanyFilter,
    setSelectedProjectId,
    setSelectedProject,
    setSelectedRole,
    setSelectedAdvisor,
    setSelectedAdvisorId,
    clearSelection,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access tenant context
 *
 * @example
 * ```tsx
 * const {
 *   isPlatform,
 *   selectedProject,
 *   pipelineStages,
 *   canShowPipelineStats,
 *   setSelectedProjectId,
 * } = useTenant();
 *
 * // Stats are automatically available when project is selected
 * {canShowPipelineStats && (
 *   <PipelineStats stages={pipelineStages} />
 * )}
 * ```
 */
export function useTenant(): TenantContextState {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Safe hook to access tenant context without throwing errors.
 * Returns null for selectedProject if used outside TenantProvider.
 *
 * Use this in hooks/components that may render before TenantProvider
 * or need graceful fallback behavior (like usePermissions).
 *
 * @example
 * ```tsx
 * const { selectedProject } = useTenantSafe();
 * const projectId = selectedProject?.id || null;
 * ```
 */
export function useTenantSafe(): Pick<TenantContextState, 'selectedProject'> {
  const context = useContext(TenantContext);
  return {
    selectedProject: context?.selectedProject ?? null,
  };
}

// ============================================================================
// COMPATIBILITY HOOKS
// ============================================================================

/**
 * @deprecated Use useTenant() instead
 * Compatibility hook for components using old ProjectContext pattern
 */
export function useTenantProject() {
  const { selectedProject, projects, isLoadingProjects, setSelectedProject } = useTenant();
  return {
    currentProject: selectedProject,
    availableProjects: projects,
    isLoading: isLoadingProjects,
    setCurrentProject: setSelectedProject,
    hasMultipleProjects: projects.length > 1,
    shouldShowSelector: projects.length > 1,
  };
}

/**
 * @deprecated Use useTenant() instead
 * Compatibility hook for components using old usePipelineStages pattern
 */
export function useTenantPipelineStages() {
  const { pipelineStages, isLoadingPipeline, currentPipeline } = useTenant();
  return {
    data: pipelineStages,
    isLoading: isLoadingPipeline,
    pipeline: currentPipeline,
  };
}
